/**
 * Dialog for editing an existing team's name and settings.
 * Slug cannot be changed after team creation.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";


interface Team {
	id: string;
	name: string;
	slug: string;
	settings: {
		timezone?: string;
		dateFormat?: string;
		currency?: string;
	} | null;
}

interface EditTeamDialogProps {
	team: Team;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

/**
 * Dialog component for editing team name and settings
 * @param props - Component props
 * @param props.team - The team to edit
 * @param props.open - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.onSuccess - Callback when team is updated successfully
 */
export function EditTeamDialog({
	team,
	open,
	onOpenChange,
	onSuccess,
}: EditTeamDialogProps) {
	const [name, setName] = useState(team.name);
	const [timezone, setTimezone] = useState(team.settings?.timezone ?? "");
	const [dateFormat, setDateFormat] = useState(team.settings?.dateFormat ?? "");
	const [currency, setCurrency] = useState(team.settings?.currency ?? "");

	// Reset form when team changes
	useEffect(() => {
		setName(team.name);
		setTimezone(team.settings?.timezone ?? "");
		setDateFormat(team.settings?.dateFormat ?? "");
		setCurrency(team.settings?.currency ?? "");
	}, [team]);

	// Update team mutation
	const updateMutation = trpc.teams.update.useMutation({
		onSuccess: () => {
			onSuccess();
		},
	});

	/**
	 * Handles form submission to update team
	 * @param e - Form submit event
	 */
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		const settings =
			timezone || dateFormat || currency
				? {
					...(timezone && { timezone }),
					...(dateFormat && { dateFormat }),
					...(currency && { currency }),
				}
				: undefined;

		updateMutation.mutate({
			teamId: team.id,
			name: name.trim(),
			settings,
		});
	};

	/**
	 * Handles dialog open state change and resets mutation on close
	 * @param newOpen - New dialog open state
	 */
	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			updateMutation.reset();
		}
		onOpenChange(newOpen);
	};

	const hasChanges =
		name !== team.name ||
		timezone !== (team.settings?.timezone ?? "") ||
		dateFormat !== (team.settings?.dateFormat ?? "") ||
		currency !== (team.settings?.currency ?? "");

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Edit Team</DialogTitle>
					<DialogDescription>
						Update team settings. Slug cannot be changed after creation.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit}>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="name">Team Name</Label>
							<Input
								id="name"
								value={name}
								onChange={(e) => { setName(e.target.value); }}
								placeholder="e.g., Marketing Team"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="slug">Slug</Label>
							<Input id="slug" value={team.slug} disabled />
							<p className="text-xs text-muted-foreground">
								Slug cannot be changed after creation
							</p>
						</div>

						{/* Settings Section */}
						<div className="border-t pt-4 mt-4">
							<p className="text-sm font-medium mb-3">
								Settings{" "}
								<span className="text-muted-foreground font-normal">
									(optional)
								</span>
							</p>
							<div className="space-y-3">
								<div className="space-y-2">
									<Label htmlFor="timezone">Timezone</Label>
									<Input
										id="timezone"
										value={timezone}
										onChange={(e) => { setTimezone(e.target.value); }}
										placeholder="e.g., America/New_York"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="dateFormat">Date Format</Label>
									<Input
										id="dateFormat"
										value={dateFormat}
										onChange={(e) => { setDateFormat(e.target.value); }}
										placeholder="e.g., MM/DD/YYYY"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="currency">Currency</Label>
									<Input
										id="currency"
										value={currency}
										onChange={(e) => { setCurrency(e.target.value); }}
										placeholder="e.g., USD"
									/>
								</div>
							</div>
						</div>

						{/* Error */}
						{updateMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
								{updateMutation.error.message}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => { handleOpenChange(false); }}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!name.trim() || !hasChanges || updateMutation.isPending}
						>
							{updateMutation.isPending ? "Saving..." : "Save Changes"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
